import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Patient, Practitioner, Provenance } from 'fhir/r4'
import { makeName } from '../utils'
import { getProvenanceData } from '../../patients/utils'
import { startSxpProxy } from '../../../utils/api'
import { REGISTRATION_PROJECT_ID } from '../../../utils/constants'
import ProvenanceView from '../../../components/ProvenanceView'
import { ProvenanceData } from '../../patients/patientsSlice'
import LocationByPrefix from '../../patients/dashboard/LocationByPrefix'
import '../../../features/lms/lms.scss'

type Props = {
  patient: Patient
}

const PatientInfo = ({ patient }: Props) => {
  const [provenances, setProvenances] = useState<
    Record<string, ProvenanceData>
  >({})
  const ids = patient ? patient.id : undefined

  useEffect(() => {
    if (ids) {
      startSxpProxy(REGISTRATION_PROJECT_ID, 'fetchProvenance', {
        patientIds: ids,
      })
        .then((data) => {
          const entries =
            data?.data?.entry?.map(
              (dde: { resource: Provenance | Practitioner }) => dde?.resource
            ) ?? []
          const obj = getProvenanceData(entries)
          setProvenances(obj)
        })
        .catch((err) => console.log(err))
    }
  }, [ids])

  if (!patient) return null
  return (
    <div className='patient-info'>
      <div className='grid'>
        <div className='field'>
          <span className='label'>Patient Name:</span>
          <span className='value'>{makeName(patient?.name) || 'N/A'}</span>
        </div>

        <div className='field'>
          <span className='label'>UHID:</span>
          <span className='value'>
            {patient.identifier?.[2]?.value ??
              patient.identifier?.[0]?.value ??
              '-'}
          </span>
        </div>

        <div className='field'>
          <span className='label'>Gender:</span>
          <span className='value'>{patient?.gender || 'N/A'}</span>
        </div>

        <div className='field'>
          <span className='label'>Age:</span>
          <span className='value'>
            {patient?.birthDate
              ? `${
                  new Date().getFullYear() -
                  new Date(patient.birthDate).getFullYear()
                } years`
              : 'N/A'}
          </span>
        </div>

        <div className='field'>
          <span className='label'>Contact:</span>
          <span className='value'>
            {patient?.telecom?.find((t: any) => t?.system === 'phone')?.value ||
              'N/A'}
          </span>
        </div>

        <div className='field'>
          <span className='label'>Location:</span>
          <span className='value'>
            <LocationByPrefix
              id={
                patient?.identifier?.[2]?.value ??
                patient?.identifier?.[0]?.value ??
                '-'
              }
            />
          </span>
        </div>

        <div className='field'>
          <span className='label'>Created:</span>
          <span className='value'>
            <ProvenanceView
              date={provenances[ids ?? '']?.createdOn ?? ''}
              name={provenances[ids ?? '']?.createdBy ?? 'Unknown'}
              mode={'CREATE'}
            />
          </span>
        </div>

        <div className='field'>
          <span className='label'>Updated:</span>
          <span className='value'>
            <ProvenanceView
              date={
                provenances[ids ?? '']?.updatedOn ??
                provenances[ids ?? '']?.createdOn ??
                ''
              }
              name={
                provenances[ids ?? '']?.updatedBy ??
                provenances[ids ?? '']?.createdBy ??
                'Unknown'
              }
              mode={'UPDATE'}
            />
          </span>
        </div>
      </div>
    </div>
  )
}

PatientInfo.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.string.isRequired,
    birthDate: PropTypes.string,
    identifier: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
      })
    ),
    name: PropTypes.array,
    gender: PropTypes.string,
    telecom: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
      })
    ),
  }),
}

export default PatientInfo
